import { loadProgress, saveProgress, getWeaponLevel, weaponUpgradeCost, upgradeMainWeapon } from '../shared/progress';
import { ALL_WEAPON_META, DEFAULT_WEAPON_ID, resolveWeaponId } from '../game/weapons/registry';
import type { WeaponStats } from '../game/weapons/registry';
import { weaponLevelDamageMult } from '../game/weapons/types';
import type { PlayerProgress } from '../shared/types';

/**
 * 主武器選擇 + 升級畫面 (R-005 P2, docs/spec/WEAPON_SYSTEM_SPEC.md §5).
 *
 * 純遊戲端畫面 (DOM) — 出戰前選主武器 + 每把可花素材升級。
 *
 * ── 鐵則 (WEAPON_SYSTEM_SPEC §0 鐵則放寬 + 學習/遊戲分離) ──
 *   - 武器脫離「學習=戰力」:威力 = 武器等級曲線 (weaponLevelDamageMult),**不讀修煉值**。
 *   - 選武器 / 升級**絕不**彈題目、**絕不**寫 factionXP/freeXP/skillLevels。
 *   - 選擇只寫 selectedWeapon;升級花 weaponShards → weaponLevels[id]++ (progress 函式代勞)。
 *   - 未解鎖武器顯示剪影 (locked),不可選 (P2 預設全解鎖,剪影主要為 P3 掉落收集鋪路)。
 *
 * registry 是唯一真相:列表動態讀 ALL_WEAPON_META,P4 新增主武器自動出現。
 */

export interface WeaponSelectOptions {
  /** 確認/返回後呼叫 (通常 → home 或 loadout)。 */
  onBack: () => void;
}

/** 主武器收集數 N/總 (unlockedWeapons ∩ registry / registry 總)。純讀。 */
export function weaponCollectionCount(progress: PlayerProgress | null): { owned: number; total: number } {
  const total = ALL_WEAPON_META.length;
  if (!progress) return { owned: 0, total };
  const unlocked = new Set(progress.unlockedWeapons ?? []);
  // 預設武器 (rapid-cannon) 永遠視為已擁有 (resolveWeaponId 硬保)。
  let owned = 0;
  for (const w of ALL_WEAPON_META) {
    if (w.id === DEFAULT_WEAPON_ID || unlocked.has(w.id)) owned++;
  }
  return { owned, total };
}

/** 一把主武器是否已解鎖 (預設武器永遠解鎖)。純讀。 */
export function isWeaponUnlocked(progress: PlayerProgress | null, weaponId: string): boolean {
  if (weaponId === DEFAULT_WEAPON_ID) return true;
  if (!progress) return false;
  return (progress.unlockedWeapons ?? []).includes(weaponId);
}

export function renderWeaponSelect(container: HTMLElement, options: WeaponSelectOptions): void {
  const root = document.createElement('div');
  root.className = 'weapon-select';
  root.setAttribute('data-testid', 'weapon-select');
  root.style.cssText = 'display:flex;flex-direction:column;align-items:center;padding:20px 14px 40px;color:#fff;';
  container.appendChild(root);

  function render(): void {
    const progress = loadProgress();
    if (!progress) { root.remove(); options.onBack(); return; }

    root.innerHTML = '';

    // ── 標題 + 收藏數 ──
    const { owned, total } = weaponCollectionCount(progress);
    const header = document.createElement('div');
    header.className = 'weapon-select-header';
    header.style.cssText = 'width:100%;max-width:560px;text-align:center;margin-bottom:8px;';
    header.innerHTML = `
      <h1 style="margin:0 0 4px;font-size:24px;">選擇主武器</h1>
      <p style="margin:0;opacity:0.82;font-size:13px;">挑一把當主武器 — 不同武器玩法不同,每把可升級變強</p>
    `;
    const collect = document.createElement('div');
    collect.className = 'weapon-collection';
    collect.setAttribute('data-testid', 'weapon-collection');
    collect.setAttribute('data-owned', String(owned));
    collect.setAttribute('data-total', String(total));
    collect.style.cssText = 'margin-top:8px;font-size:13px;font-weight:700;color:#ffd54f;';
    collect.textContent = `🗂️ 收藏 ${owned} / ${total}`;
    header.appendChild(collect);

    // 素材餘額。
    const shards = progress.weaponShards ?? 0;
    const shardBadge = document.createElement('div');
    shardBadge.className = 'weapon-shard-balance';
    shardBadge.setAttribute('data-testid', 'weapon-shard-balance');
    shardBadge.setAttribute('data-shards', String(shards));
    shardBadge.style.cssText = 'margin-top:4px;font-size:13px;font-weight:700;color:#80d8ff;';
    shardBadge.textContent = `🔧 武器素材 ${shards}`;
    header.appendChild(shardBadge);
    root.appendChild(header);

    const selectedId = resolveWeaponId(progress.selectedWeapon, progress.unlockedWeapons);

    const grid = document.createElement('div');
    grid.className = 'weapon-card-grid';
    grid.setAttribute('data-testid', 'weapon-card-grid');
    grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;justify-content:center;margin-top:16px;max-width:600px;';
    root.appendChild(grid);

    for (const meta of ALL_WEAPON_META) {
      grid.appendChild(buildCard(meta.id, progress, selectedId, render));
    }

    const back = document.createElement('button');
    back.className = 'secondary-btn weapon-select-back';
    back.setAttribute('data-testid', 'weapon-select-back');
    back.textContent = '← 返回';
    back.style.cssText = 'margin-top:22px;padding:10px 22px;border-radius:10px;border:1px solid #4a4a5e;background:#2a2a38;color:#fff;cursor:pointer;font-size:15px;';
    back.addEventListener('click', () => { root.remove(); options.onBack(); });
    root.appendChild(back);
  }

  render();
}

/** 建一張武器卡 (含選擇 + 升級)。re-render 重畫整個畫面 (簡單一致)。 */
function buildCard(
  weaponId: string,
  progress: PlayerProgress,
  selectedId: string,
  rerender: () => void,
): HTMLElement {
  const meta = ALL_WEAPON_META.find((m) => m.id === weaponId)!;
  const unlocked = isWeaponUnlocked(progress, weaponId);
  const selected = selectedId === weaponId;
  const level = getWeaponLevel(progress, weaponId);
  const shards = progress.weaponShards ?? 0;
  const cost = weaponUpgradeCost(level);
  const curMult = weaponLevelDamageMult(level);
  const nextMult = weaponLevelDamageMult(level + 1);

  const card = document.createElement('div');
  card.className = 'weapon-card'
    + (selected ? ' weapon-card-selected' : '')
    + (unlocked ? '' : ' weapon-card-locked');
  card.setAttribute('data-testid', `weapon-card-${weaponId}`);
  card.setAttribute('data-weapon-id', weaponId);
  card.setAttribute('data-level', String(level));
  if (!unlocked) card.setAttribute('data-locked', 'true');
  if (selected) card.setAttribute('data-selected', 'true');
  card.style.cssText = [
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'width:170px',
    'padding:14px 12px',
    'border-radius:16px',
    'background:#1e1e2a',
    `border:3px solid ${selected ? '#ffd54f' : 'transparent'}`,
    'box-shadow:0 4px 12px rgba(0,0,0,0.3)',
    'text-align:center',
    'position:relative',
    unlocked ? 'opacity:1' : 'opacity:0.5',
  ].join(';');

  // ── emoji + 名稱 + 一句話玩法 ──
  const emoji = document.createElement('div');
  emoji.className = 'weapon-card-emoji';
  emoji.style.cssText = 'font-size:42px;line-height:1;'
    + (unlocked ? '' : 'filter:grayscale(1) brightness(0.6);');
  emoji.textContent = unlocked ? meta.emoji : '❔';
  card.appendChild(emoji);

  const name = document.createElement('div');
  name.className = 'weapon-card-name';
  name.style.cssText = 'font-size:17px;font-weight:700;margin-top:8px;';
  name.textContent = unlocked ? meta.displayName : '???';
  card.appendChild(name);

  const blurb = document.createElement('div');
  blurb.className = 'weapon-card-blurb';
  blurb.style.cssText = 'font-size:12px;margin-top:6px;opacity:0.85;min-height:32px;';
  blurb.textContent = unlocked ? meta.blurb : '尚未收集';
  card.appendChild(blurb);

  if (!unlocked) {
    const lock = document.createElement('div');
    lock.className = 'weapon-card-lock';
    lock.style.cssText = 'position:absolute;top:8px;right:8px;font-size:20px;';
    lock.textContent = '🔒';
    card.appendChild(lock);
    return card;
  }

  // ── 武器數值 (攻擊力/射速/範圍 星級 + 特效一句話) ──
  // 純展示,讓玩家一眼看出每把差別 (boss 2026-06-13「不同攻擊應該都要有區別」)。
  card.appendChild(buildStatsBlock(weaponId, meta.stats));

  // ── 等級 + 威力 ──
  const lv = document.createElement('div');
  lv.className = 'weapon-card-level';
  lv.setAttribute('data-testid', `weapon-card-level-${weaponId}`);
  lv.style.cssText = 'margin-top:8px;font-size:13px;font-weight:700;color:#80d8ff;';
  lv.textContent = `Lv ${level}　威力 ×${curMult.toFixed(2)}`;
  card.appendChild(lv);

  // ── 升級鈕 (顯示前→後威力 + 花費) ──
  const canAfford = shards >= cost;
  const upBtn = document.createElement('button');
  upBtn.className = 'weapon-upgrade-btn';
  upBtn.setAttribute('data-testid', `weapon-upgrade-${weaponId}`);
  upBtn.disabled = !canAfford;
  upBtn.style.cssText = [
    'margin-top:10px',
    'width:100%',
    'padding:8px 6px',
    'border-radius:10px',
    'border:none',
    'font-size:13px',
    'font-weight:700',
    canAfford ? 'cursor:pointer' : 'cursor:not-allowed',
    canAfford ? 'background:#43a047' : 'background:#3a3a48',
    canAfford ? 'color:#fff' : 'color:#888',
  ].join(';');
  // 升級前→後威力預覽 (spec §5「升級畫面顯示前→後」)。
  upBtn.innerHTML = `升級 🔧${cost}<br><span style="font-size:11px;font-weight:600;opacity:0.92;">威力 ×${curMult.toFixed(2)} → ×${nextMult.toFixed(2)}</span>`;
  upBtn.addEventListener('click', () => {
    const p = loadProgress();
    if (!p) return;
    const res = upgradeMainWeapon(p, weaponId);
    if (res.ok) saveProgress(p);
    rerender();
  });
  card.appendChild(upBtn);

  if (!canAfford) {
    const hint = document.createElement('div');
    hint.className = 'weapon-upgrade-hint';
    hint.style.cssText = 'margin-top:6px;font-size:11px;color:#ffb74d;';
    hint.textContent = '素材不足 — 打怪取得';
    card.appendChild(hint);
  }

  // ── 選擇鈕 / 已選標記 ──
  if (selected) {
    const badge = document.createElement('div');
    badge.className = 'weapon-card-current';
    badge.setAttribute('data-testid', `weapon-card-current-${weaponId}`);
    badge.style.cssText = 'margin-top:10px;font-size:13px;font-weight:700;color:#ffd54f;';
    badge.textContent = '✓ 目前主武器';
    card.appendChild(badge);
  } else {
    const selBtn = document.createElement('button');
    selBtn.className = 'weapon-select-btn';
    selBtn.setAttribute('data-testid', `weapon-select-${weaponId}`);
    selBtn.textContent = '選為主武器';
    selBtn.style.cssText = 'margin-top:10px;width:100%;padding:8px 6px;border-radius:10px;border:1px solid #ffd54f;background:transparent;color:#ffd54f;cursor:pointer;font-size:13px;font-weight:700;';
    selBtn.addEventListener('click', () => {
      selectMainWeapon(weaponId);
      rerender();
    });
    card.appendChild(selBtn);
  }

  return card;
}

/** 把 1~5 星級畫成 ★/☆ 字串 (K-3 看得懂的數值溫度計)。clamp 到 0~5。 */
export function starString(value: number): string {
  const n = Number.isFinite(value) ? Math.max(0, Math.min(5, Math.round(value))) : 0;
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

/**
 * 建一把武器的「數值區塊」(攻擊力/射速/範圍 星級 + 特效一句話)。
 * 純展示 DOM,讓玩家一眼看出每把武器差別 (boss 2026-06-13)。照本檔既有深色 token。
 */
function buildStatsBlock(weaponId: string, stats: WeaponStats): HTMLElement {
  const box = document.createElement('div');
  box.className = 'weapon-card-stats';
  box.setAttribute('data-testid', `weapon-card-stats-${weaponId}`);
  box.setAttribute('data-power', String(stats.power));
  box.setAttribute('data-firerate', String(stats.fireRate));
  box.setAttribute('data-range', String(stats.range));
  box.style.cssText = 'width:100%;margin-top:8px;padding:8px 10px;border-radius:10px;background:#16161f;font-size:11px;line-height:1.6;text-align:left;';

  const row = (label: string, value: number): string =>
    `<div style="display:flex;justify-content:space-between;gap:6px;">`
    + `<span style="opacity:0.8;">${label}</span>`
    + `<span style="color:#ffd54f;letter-spacing:1px;font-weight:700;">${starString(value)}</span>`
    + `</div>`;

  box.innerHTML =
    row('攻擊力', stats.power)
    + row('射速', stats.fireRate)
    + row('範圍', stats.range)
    + `<div class="weapon-card-special" style="margin-top:6px;color:#80d8ff;font-weight:700;">✦ ${stats.special}</div>`;

  return box;
}

/**
 * 寫入 selectedWeapon 並存檔。抽出來方便測試。
 * 鐵則:只寫 selectedWeapon (玩遊戲軌),絕不碰戰力欄位。未解鎖 → no-op (防呆)。
 */
export function selectMainWeapon(weaponId: string): void {
  const progress = loadProgress();
  if (!progress) return;
  if (!isWeaponUnlocked(progress, weaponId)) return;
  progress.selectedWeapon = weaponId;
  saveProgress(progress);
}
